import targetLogo from "/img/integration/target.png"
import {
  findIntegrationByName,
  getIntegrations,
  getIntegrationsWithJobs,
  upsertIntegration,
  IntegrationWithJobInfo,
} from "@/api/integration"
import { ConnectMailboxButtons } from "@/connect_mailbox/connect_mailbox_buttons"
import { PasswordInput } from "@/components/form/Input.tsx"
import { Button } from "@/components/ui/button.tsx"
import { useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import { use_connect_redirect_to } from "../use_connect_redirect_to"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { CheckCircleIcon } from "@heroicons/react/20/solid"
import { useAsyncEffect } from "@/utils"
import { DateTime } from "luxon"
import useSWR from "swr"
import { SyncStatusDisplay } from "@/settings/integrations/sync-status-row"

// The Partners Online sync has its own row for its schedule and status. It holds no login: the
// sync signs in with the Partners Online user saved on `target` by the steps below.
export const TARGET_PORTAL_NAME = "target-portal"
const PARTNERS_ONLINE_URL = "https://www.partnersonline.com"

function StepIndicator({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  return (
    <div class="flex items-center justify-center gap-1.5 mb-6">
      {Array.from({ length: totalSteps }, (_, i) => (
        <div
          key={i}
          class={`h-1.5 w-6 rounded-full transition-colors ${
            i < currentStep ? "bg-gray-900" : i === currentStep ? "bg-gray-400" : "bg-gray-200"
          }`}
        />
      ))}
    </div>
  )
}

/** Credential payload for POST /integration when connecting Target (Partners Online password). */
export function buildTargetIntegrationCredentials(password: string): Record<string, string> {
  return { password: password.trim() }
}

/**
 * What the Partners Online sync reads, and how its last run went. It signs in with the login
 * saved on the Target connection, so there is nothing to enter here: saving that login is what
 * switches the sync on. Loads the `target-portal` row itself.
 */
export function TargetPortalSyncSection({ className }: { className?: string }) {
  // with-jobs rather than the plain list: `/integration` returns bare rows with no `last_sync_job`.
  const { data: integrations_result } = useSWR("integrations-with-jobs", () => getIntegrationsWithJobs())
  const portal = integrations_result?.ok
    ? findIntegrationByName(integrations_result.value.data, TARGET_PORTAL_NAME)
    : undefined

  return (
    <div class={`space-y-4 ${className ?? ""}`}>
      {portal ? (
        <SyncStatusDisplay lastSyncJob={portal.last_sync_job} integrationName={TARGET_PORTAL_NAME} />
      ) : (
        <p class="text-sm text-gray-700">Not switched on yet. Saving the Partners Online login for Target switches it on.</p>
      )}
      <p class="text-sm text-gray-500">
        With the{" "}
        <a href={PARTNERS_ONLINE_URL} target="_blank" rel="noopener noreferrer" class="text-blue-700 hover:underline">
          partnersonline.com
        </a>{" "}
        login saved for Target, we sign in on weekdays and read your Target payments line by line, the backup behind
        promotional and vendor income charges, compliance chargebacks and receiving chargebacks, and your dispute
        history in Synergy. We only read: nothing is filed or changed in Partners Online.
      </p>
      <p class="text-sm text-gray-500">
        Target emails a one-time passcode at every sign-in and we read it from your connected mailbox, so the login's
        email address must deliver to a mailbox connected to MarginWiz. A Partners Online user created for this sync
        works best, with access to Accounts Payable: Domestic, Target Vendor Income Reports, Supplier Performance
        Management Dashboard and Synergy.
      </p>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ConnectTargetPage(props: any = {}) {
  const redirectTo = use_connect_redirect_to(props.redirectTo)
  const [, setLocation] = useLocation()
  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [integrations, setIntegrations] = useState<IntegrationWithJobInfo[]>([])
  const [selectedEmail, setSelectedEmail] = useState<string>("")
  const [useCustomEmail, setUseCustomEmail] = useState(false)
  const [customEmail, setCustomEmail] = useState("")
  const [portalPassword, setPortalPassword] = useState("")

  const currentPath = window.location.pathname

  async function reloadIntegrations() {
    const res = await getIntegrations()
    if (!res.ok) {
      console.error(res.error)
      return
    }
    setIntegrations(res.value.data)
  }

  useAsyncEffect(reloadIntegrations, [])

  const emailAccounts = integrations.filter(i => ["gmail", "microsoft"].includes(i.name))

  function handleEmailContinue() {
    if (emailAccounts.length === 0) return
    // Use the most recently connected email
    const sorted = [...emailAccounts].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
    setSelectedEmail(sorted[0].username)
    setStep(1)
  }

  async function handleConfirm() {
    const email = useCustomEmail ? customEmail : selectedEmail
    if (!email) return
    if (!portalPassword.trim()) return

    setLoading(true)

    const res = await upsertIntegration("target", email, buildTargetIntegrationCredentials(portalPassword))

    setLoading(false)
    if (!res.ok) {
      console.error("Failed to create Target integration")
      return
    }

    setCompleted(true)
  }

  if (completed) {
    return (
      <CenterColumnContainer>
        <div class="text-center space-y-6">
          <img src={targetLogo} alt="Target" class="h-12 object-contain mx-auto" />
          <div class="flex justify-center">
            <CheckCircleIcon class="h-12 w-12 text-green-500" />
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Connection Submitted</h3>
            <p class="text-gray-500 text-sm mt-1">
              We'll confirm the connection and reach out if there are any issues.
            </p>
          </div>
          <Button
            class="w-full bg-gray-900 text-white rounded-lg hover:bg-gray-800"
            onClick={() => setLocation(redirectTo)}>
            Back to Connections
          </Button>
        </div>
      </CenterColumnContainer>
    )
  }

  return (
    <CenterColumnContainer>
      <div class="space-y-6">
        <img src={targetLogo} alt="Target" class="h-10 object-contain mx-auto" />
        <StepIndicator currentStep={step} totalSteps={5} />

        {step === 0 && (
          <div class="space-y-6">
            <p class="text-gray-500 text-sm">
              First, link the email account associated with the Target account that you'll give MarginWiz access to.
            </p>

            <ConnectMailboxButtons microsoftLabel="Connect Microsoft Office 365" onChange={reloadIntegrations} />

            {emailAccounts.length > 0 && (
              <div class="border border-gray-200 rounded-lg p-4">
                <h3 class="font-medium text-gray-900 text-sm mb-3">Connected Accounts</h3>
                <div class="space-y-2">
                  {emailAccounts.map(e => {
                    let updated = ""
                    if (e.session_updated_at) {
                      const d = DateTime.fromISO(e.session_updated_at)
                      updated = d.toLocaleString(DateTime.DATETIME_MED)
                    }
                    return (
                      <div key={e.username} class="flex justify-between gap-4 text-sm">
                        <span class="text-gray-700 truncate">{e.username}</span>
                        <span class="text-gray-400 whitespace-nowrap">{updated}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            <Button className="w-full" onClick={handleEmailContinue} disabled={emailAccounts.length === 0}>
              Continue
            </Button>

            {emailAccounts.length === 0 && (
              <p class="text-sm text-gray-400 text-center">Connect at least one email account to continue</p>
            )}
          </div>
        )}

        {step === 1 && (
          <div class="space-y-4">
            <p class="text-gray-500 text-sm">Select or enter the email address for the Target user:</p>
            <div class="space-y-2">
              {emailAccounts.map(e => (
                <label
                  key={e.username}
                  class={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                    !useCustomEmail && selectedEmail === e.username
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}>
                  <input
                    type="radio"
                    name="email"
                    value={e.username}
                    checked={!useCustomEmail && selectedEmail === e.username}
                    onChange={() => {
                      setUseCustomEmail(false)
                      setSelectedEmail(e.username)
                    }}
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span class="ml-3 text-sm text-gray-700">{e.username}</span>
                </label>
              ))}
              <label
                class={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                  useCustomEmail ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                }`}>
                <input
                  type="radio"
                  name="email"
                  checked={useCustomEmail}
                  onChange={() => setUseCustomEmail(true)}
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span class="ml-3 text-sm text-gray-700">Other email</span>
              </label>
              {useCustomEmail && (
                <input
                  type="email"
                  value={customEmail}
                  onInput={e => setCustomEmail((e.target as HTMLInputElement).value)}
                  placeholder="Enter email address"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
            </div>

            <div class="flex gap-3 justify-between">
              <Button variant="outline" class="flex-1" onClick={() => setStep(0)}>
                Back
              </Button>
              <Button
                class="flex-1 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                onClick={() => setStep(2)}
                disabled={useCustomEmail ? !customEmail : !selectedEmail}>
                Continue
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div class="space-y-4">
            <p class="text-gray-500 text-sm">
              If an account for {useCustomEmail ? customEmail : selectedEmail} already exists, press Continue.
            </p>
            <p class="text-gray-500 text-sm">
              Navigate to the Target Partners Online portal and go to user settings. Create a new user for{" "}
              <span class="font-semibold">{useCustomEmail ? customEmail : selectedEmail}</span>.
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.open("https://www.partnersonline.com/", "_blank")}>
              Open Target Portal
            </Button>
            <div class="flex gap-3 justify-between">
              <Button variant="outline" class="flex-1" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button class="flex-1 bg-gray-900 text-white rounded-lg hover:bg-gray-800" onClick={() => setStep(3)}>
                Continue
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div class="space-y-4">
            <p class="text-gray-500 text-sm">
              Enter the password for the Partners Online user{" "}
              <span class="font-semibold">{useCustomEmail ? customEmail : selectedEmail}</span>.
            </p>
            <PasswordInput
              label="Target portal password"
              name="target_portal_password"
              required={true}
              value={portalPassword}
              onChange={e => setPortalPassword(e.currentTarget.value)}
              noAutofill
            />
            <div class="flex gap-3 justify-between">
              <Button variant="outline" class="flex-1" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button
                class="flex-1 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                onClick={() => setStep(4)}
                disabled={!portalPassword.trim()}>
                Continue
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div class="space-y-4">
            <p class="text-gray-500 text-sm">Please confirm that you have completed the setup:</p>
            <div class="bg-gray-50 rounded-lg p-4">
              <p class="text-sm text-gray-700">
                I've created a user for{" "}
                <span class="font-semibold">{useCustomEmail ? customEmail : selectedEmail}</span> on Partners Online.
              </p>
            </div>
            <div class="flex gap-3 justify-between">
              <Button variant="outline" class="flex-1" onClick={() => setStep(3)}>
                Back
              </Button>
              <Button
                class="flex-1 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300"
                onClick={handleConfirm}
                disabled={loading}>
                {loading ? "Confirming..." : "Confirm"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </CenterColumnContainer>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("buildTargetIntegrationCredentials", () => {
    it("trims and includes password for API credential payload", () => {
      expect(buildTargetIntegrationCredentials(" secret ")).toEqual({ password: "secret" })
    })
  })

  describe("the Partners Online sync row", () => {
    it("is its own integration beside target", () => {
      expect(TARGET_PORTAL_NAME).toBe("target-portal")
    })
  })
}
